import React, { type ComponentPropsWithoutRef, useState, useCallback } from 'react';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { getAnalyticsAttributesFromExtension } from '@atlassian/jira-forge-ui-analytics/src/common/utils/get-analytics-attributes-from-extension/index.tsx';
import { fireTrackEditedEvent } from '@atlassian/jira-forge-ui-analytics/src/services/custom-field-edit/index.tsx';
import { EditAnalyticsWrapper } from '@atlassian/jira-forge-ui-analytics/src/ui/custom-field/index.tsx';
import { AsyncCustomFieldInlineEdit } from '@atlassian/jira-forge-ui-async/src/ui/custom-fields/custom-field-inline-edit/async.tsx';
import { ErrorPanel } from '@atlassian/jira-forge-ui-renderers/src/common/ui/error-panel/index.tsx';
import type { ForgeCustomFieldValue } from '@atlassian/jira-forge-ui-types/src/common/types/contexts/custom-field.tsx';
import { useIntl } from '@atlassian/jira-intl';
import type { Attributes } from '@atlassian/jira-product-analytics-bridge';
import { transformValue } from '../utils/index.tsx';
import messages from './messages.tsx';

type LazyCustomFieldProps = Pick<
	ComponentPropsWithoutRef<typeof AsyncCustomFieldInlineEdit>,
	'extension' | 'extensionData' | 'extensionPayload'
>;

type Props = LazyCustomFieldProps & {
	analyticsAttributes: Attributes;
	onChange: (value: ForgeCustomFieldValue) => void;
};

export const EditModeView = ({
	extension,
	extensionData,
	extensionPayload,
	analyticsAttributes,
	onChange,
}: Props): React.JSX.Element => {
	const [isModalMode, setIsModalMode] = useState(false);
	const intl = useIntl();
	const onEditConfirm = useCallback(
		(newValue: ForgeCustomFieldValue) => {
			onChange(transformValue(newValue, extension));
			fireTrackEditedEvent(analyticsAttributes.renderContext, analyticsAttributes);
		},
		[extension, analyticsAttributes, onChange],
	);

	const onModalRender = useCallback(() => setIsModalMode(true), []);

	if (isModalMode) {
		// temporary for UI Kit 1 version of 1.2.0 when inline edit is not supported
		// should be removed with removal of UI Kit 1
		const translatedError = {
			message: intl.formatMessage(messages.modalModeErrorTitle),
			errorMessage: intl.formatMessage(messages.modalModeErrorDescription),
		};

		log.safeErrorWithoutCustomerData(
			'jiraForgeUI.customFieldEdit',
			'App tried to render modal expierience with non-supported UI Kit 1',
			{
				extennsionId: extension.id,
			},
		);

		return <ErrorPanel error={translatedError} />;
	}

	return (
		<AsyncCustomFieldInlineEdit
			extension={extension}
			extensionData={extensionData}
			analyticsAttributes={analyticsAttributes}
			extensionPayload={extensionPayload}
			onSubmit={onEditConfirm}
			onModalRender={onModalRender}
		/>
	);
};

export const EditMode = (props: Props): React.JSX.Element => (
	<EditAnalyticsWrapper attributes={getAnalyticsAttributesFromExtension(props.extension)}>
		<EditModeView {...props} />
	</EditAnalyticsWrapper>
);
